/**
 * Dialog tạo và sửa dịch vụ danh mục: thông tin chung, bước quy trình, định mức vật tư.
 * Hủy hỏi xác nhận; nút X và Esc đóng thẳng; backdrop không đóng.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Tab, Tabs } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormDialogFooter,
  IconButton,
  attemptFormDialogClose,
  blockDialogCloseWhenBusy,
  useConfirm,
} from '@/shared/ui';
import { toast } from '@/shared/feedback/toast';
import { queryKeys } from '@/shared/query';
import { getApiErrorMessage } from '@/shared/utils/apiError';
import { treatmentCatalogAPI } from '../api/treatmentCatalog.api';
import type { TreatmentCatalogFormData, TreatmentCatalogItem } from '../types/treatmentCatalog.types';
import TreatmentCatalogFormFields from './TreatmentCatalogFormFields';
import { useTreatmentJournalMode } from '@/shared/hooks/useTreatmentRecordingMode';
import {
  buildTreatmentCatalogFormSchema,
  catalogFormTabForFieldErrors,
  createEmptyTreatmentCatalogForm,
  toCreateCatalogPayload,
  toUpdateCatalogPayload,
  type CatalogFormTab,
} from '../utils/treatmentCatalogItemForm';

const CATALOG_FORM_DIALOG_PAPER_SX = {
  width: 900,
  height: 720,
  maxWidth: '100%',
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
} as const;

export type TreatmentCatalogDialogMode = 'create' | 'edit';

export interface TreatmentCatalogItemFormDialogProps {
  mode: TreatmentCatalogDialogMode;
  title?: string;
  catalogItemId?: string;
  initialValues?: TreatmentCatalogFormData;
  loading?: boolean;
  externalError?: string | null;
  onClose: () => void;
  onSuccess?: (item: TreatmentCatalogItem, mode: TreatmentCatalogDialogMode) => void;
}

const TreatmentCatalogItemFormDialog: React.FC<TreatmentCatalogItemFormDialogProps> = ({
  mode,
  title,
  catalogItemId,
  initialValues,
  loading = false,
  externalError = null,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation(['treatment', 'common']);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const journalMode = useTreatmentJournalMode();
  const [tab, setTab] = useState<CatalogFormTab>('general');

  useEffect(() => {
    if (journalMode && tab === 'steps') setTab('general');
  }, [journalMode, tab]);
  const [apiError, setApiError] = useState<string | null>(null);

  const schema = useMemo(() => buildTreatmentCatalogFormSchema(t), [t]);
  const form = useForm<TreatmentCatalogFormData>({
    defaultValues: createEmptyTreatmentCatalogForm(),
    resolver: yupResolver(schema) as unknown as Resolver<TreatmentCatalogFormData>,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, reset } = form;

  const showForm = mode === 'create' || Boolean(initialValues);

  useEffect(() => {
    if (mode === 'create') {
      reset(createEmptyTreatmentCatalogForm());
      setApiError(null);
      return;
    }
    if (initialValues) {
      reset(initialValues);
      setApiError(null);
    }
  }, [mode, initialValues, reset]);

  const saveMutation = useMutation({
    mutationFn: async (values: TreatmentCatalogFormData) => {
      const payload =
        mode === 'edit' ? toUpdateCatalogPayload(values) : toCreateCatalogPayload(values);
      if (mode === 'edit' && catalogItemId) {
        const response = await treatmentCatalogAPI.update(catalogItemId, payload);
        if (!response.data) throw new Error(t('toasts.updateCatalogItemFailed'));
        return response.data;
      }
      const response = await treatmentCatalogAPI.create(payload);
      if (!response.data) throw new Error(t('toasts.addCatalogItemFailed'));
      return response.data;
    },
    onSuccess: (item) => {
      toast.success(
        mode === 'edit' ? t('toasts.updateCatalogItemSuccess') : t('toasts.addCatalogItemSuccess')
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.treatmentCatalog.all });
      onSuccess?.(item, mode);
    },
    onError: (err: unknown) => {
      const message = getApiErrorMessage(
        err,
        mode === 'edit' ? t('toasts.updateCatalogItemFailed') : t('toasts.addCatalogItemFailed')
      );
      setApiError(message);
      toast.error(message);
    },
  });

  const busy = saveMutation.isPending;
  const fieldsDisabled = busy || loading;
  const dialogTitle =
    title ?? (mode === 'edit' ? t('catalog.editTitle') : t('catalog.addTitle'));
  const displayError = apiError ?? externalError;

  const dismiss = (): void => {
    if (busy) return;
    onClose();
  };

  const requestCancel = (): void => {
    void attemptFormDialogClose({
      busy,
      isDirty: true,
      confirm,
      onClose,
    });
  };

  const onValid = (values: TreatmentCatalogFormData): void => {
    setApiError(null);
    saveMutation.mutate(values);
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      scroll="paper"
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        const close = blockDialogCloseWhenBusy(busy, dismiss);
        close?.();
      }}
      aria-labelledby="treatment-catalog-form-dialog-title"
      slotProps={{ paper: { sx: CATALOG_FORM_DIALOG_PAPER_SX } }}
    >
      <DialogTitle
        id="treatment-catalog-form-dialog-title"
        sx={{
          position: 'relative',
          pr: 6,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {dialogTitle}
        <IconButton
          type="button"
          aria-label={t('common:close')}
          onClick={dismiss}
          disabled={busy}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {(loading || showForm) && (
        <Tabs
          value={tab}
          onChange={(_event, value: CatalogFormTab) => setTab(value)}
          aria-label={t('catalog.formTabsAria')}
          sx={{ px: 2, minHeight: 40, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Tab
            value="general"
            label={t('sections.generalInformation')}
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
          {journalMode ? null : (
          <Tab
            value="steps"
            label={t('sections.procedureSteps')}
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
          )}
          <Tab
            value="materials"
            label={t('sections.materialLines')}
            sx={{ minHeight: 40, textTransform: 'none' }}
          />
        </Tabs>
      )}

      <DialogContent
        dividers={false}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 2, scrollbarGutter: 'stable' }}
      >
        {displayError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayError}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && showForm ? (
          <FormProvider {...form}>
            <TreatmentCatalogFormFields
              mode={mode}
              disabled={fieldsDisabled}
              catalogItemId={catalogItemId}
              activeTab={tab}
            />
          </FormProvider>
        ) : null}
      </DialogContent>

      {!loading && showForm ? (
        <FormDialogFooter
          busy={busy}
          cancelVariant="text"
          onCancel={requestCancel}
          onSubmit={() =>
            void handleSubmit(onValid, (fieldErrors) => {
              setTab(catalogFormTabForFieldErrors(fieldErrors, { skipSteps: journalMode }));
            })()
          }
          submitLabel={mode === 'edit' ? t('actions.updateTreatment') : t('actions.saveTreatment')}
          busySubmitLabel={
            mode === 'edit' ? t('actions.updatingTreatment') : t('common:saving')
          }
        />
      ) : null}
    </Dialog>
  );
};

export default TreatmentCatalogItemFormDialog;
